/**
 * Swap transaction construction.
 *
 * The single most important property of this module is what it does **not**
 * accept: calldata. `docs/SECURITY.md` says "do not sign arbitrary calldata", and
 * the way to guarantee that is not to validate calldata handed to us — it is to
 * never accept any. Every transaction this system signs is assembled here, from
 * a fixed set of function templates, out of typed fields taken from a persisted
 * trade intent.
 *
 * That inverts the usual trust question. Instead of asking "is this calldata
 * safe?", which requires understanding every possible encoding, we ask "did we
 * build this?", which is answerable. An input that cannot be expressed as one of
 * the templates below cannot be turned into a transaction at all.
 *
 * Two supported shapes, both buying a token with the chain's native asset:
 *
 *   - Uniswap V2 `swapExactETHForTokens(amountOutMin, path, to, deadline)`
 *   - Uniswap V3 SwapRouter02 `exactInputSingle(params)`
 *
 * Selling is not implemented. The MVP buys on a signal and the operator exits
 * manually; adding a sell path means adding token approvals, which is a
 * materially larger trust surface and belongs in its own phase.
 *
 * Pure. No chain, no clock, no config — every input arrives as an argument, so
 * the entire encoding surface is testable offline. The one apparent exception,
 * `deadline`, is passed in rather than read from `Date.now()`, because a
 * transaction builder that consults a clock cannot be tested deterministically.
 */
import { encodeFunctionData, parseAbi } from 'viem';
/**
 * The V2 router surface we use. One function, deliberately.
 *
 * `swapExactETHForTokens`, not `swapExactETHForTokensSupportingFeeOnTransferTokens`:
 * the fee-on-transfer variant accepts a zero `amountOutMin` in practice because
 * the received amount is unpredictable, which would discard our slippage bound.
 * A token that needs the fee-on-transfer path is a token this system declines to
 * buy.
 */
const V2_ROUTER_ABI = parseAbi([
    'function swapExactETHForTokens(uint256 amountOutMin, address[] path, address to, uint256 deadline) payable returns (uint256[] amounts)',
]);
/**
 * SwapRouter02's `exactInputSingle`.
 *
 * Note there is no `deadline` in the struct — SwapRouter02 dropped it from the
 * params in favour of the multicall wrapper carrying one. We do not use
 * multicall, so a V3 swap has no on-chain deadline; the protection comes from
 * `amountOutMinimum` plus our own submission timeout. That asymmetry with V2 is a
 * property of the router, not a choice, and it is called out here so nobody
 * "fixes" the missing field later by inventing one.
 */
const V3_ROUTER_ABI = parseAbi([
    'struct ExactInputSingleParams { address tokenIn; address tokenOut; uint24 fee; address recipient; uint256 amountIn; uint256 amountOutMinimum; uint160 sqrtPriceLimitX96; }',
    'function exactInputSingle(ExactInputSingleParams params) payable returns (uint256 amountOut)',
]);
export class SwapBuildError extends Error {
    constructor(message) {
        super(message);
        this.name = 'SwapBuildError';
    }
}
/** Fee tiers a V3 pool can legitimately use on a standard deployment. */
export const V3_FEE_TIERS = [100, 500, 3000, 10_000];
function assertAddress(value, field) {
    if (!/^0x[0-9a-fA-F]{40}$/.test(value)) {
        throw new SwapBuildError(`${field} is not a 20-byte address: ${value}`);
    }
    return value.toLowerCase();
}
/**
 * Build the swap.
 *
 * Every guard here fails the build rather than adjusting the input. A builder
 * that silently corrects a bad amount is a builder that can sign something the
 * operator did not authorise.
 */
export function buildSwap(params) {
    const to = assertAddress(params.routerAddress, 'routerAddress');
    const tokenIn = assertAddress(params.spendTokenAddress, 'spendTokenAddress');
    const tokenOut = assertAddress(params.tokenAddress, 'tokenAddress');
    const recipient = assertAddress(params.recipient, 'recipient');
    if (params.spendAmountWei <= 0n) {
        throw new SwapBuildError('spendAmountWei must be positive');
    }
    // A zero floor means "accept any amount of tokens", which is the shape of every
    // sandwich attack. Refused outright rather than left to the caller's diligence.
    if (params.minAmountOutWei <= 0n) {
        throw new SwapBuildError('minAmountOutWei must be positive: a swap with no output floor has no slippage protection');
    }
    if (tokenIn === tokenOut) {
        throw new SwapBuildError('spendTokenAddress and tokenAddress are the same token');
    }
    if (params.protocol === 'uniswap_v2') {
        if (params.deadlineUnixSeconds <= 0n) {
            throw new SwapBuildError('deadlineUnixSeconds must be positive for a V2 swap');
        }
        const data = encodeFunctionData({
            abi: V2_ROUTER_ABI,
            functionName: 'swapExactETHForTokens',
            args: [
                params.minAmountOutWei,
                // Two hops only. A longer path routes through pools we have not assessed
                // and cannot price, so the risk score would describe a different trade
                // from the one being made.
                [tokenIn, tokenOut],
                recipient,
                params.deadlineUnixSeconds,
            ],
        });
        return {
            to,
            data,
            value: params.spendAmountWei,
            functionName: 'swapExactETHForTokens',
            protocol: 'uniswap_v2',
        };
    }
    const feeTier = params.feeTier;
    if (feeTier === null || feeTier === undefined) {
        throw new SwapBuildError('feeTier is required for a V3 swap');
    }
    if (!V3_FEE_TIERS.includes(feeTier)) {
        throw new SwapBuildError(`feeTier ${feeTier} is not a recognised Uniswap V3 tier (${V3_FEE_TIERS.join(', ')})`);
    }
    const data = encodeFunctionData({
        abi: V3_ROUTER_ABI,
        functionName: 'exactInputSingle',
        args: [
            {
                tokenIn,
                tokenOut,
                fee: feeTier,
                recipient,
                amountIn: params.spendAmountWei,
                amountOutMinimum: params.minAmountOutWei,
                // Zero means "no price limit", so `amountOutMinimum` is the only bound.
                // Setting a limit here would make the swap partially fillable, and a
                // partial fill against a fixed native value would leave dust in the
                // router.
                sqrtPriceLimitX96: 0n,
            },
        ],
    });
    return {
        to,
        data,
        value: params.spendAmountWei,
        functionName: 'exactInputSingle',
        protocol: 'uniswap_v3',
    };
}
/**
 * The slippage floor, in the token's smallest unit.
 *
 * Rounds **down**, so rounding error can only ever tighten the floor. Rounding up
 * would loosen the protection by a wei, which is harmless in size and wrong in
 * direction — and "wrong in direction" is how limits erode.
 *
 * `quotedAmountOut` comes from a router quote or a simulation, never from a price
 * feed. A quote is what this router will actually give us; a price is what some
 * other venue thinks the token is worth.
 */
export function applySlippage(quotedAmountOut, maxSlippageBps) {
    if (quotedAmountOut < 0n) {
        throw new SwapBuildError('quotedAmountOut cannot be negative');
    }
    if (!Number.isInteger(maxSlippageBps) || maxSlippageBps < 0 || maxSlippageBps > 10_000) {
        throw new SwapBuildError(`maxSlippageBps must be an integer in 0..10000, got ${maxSlippageBps}`);
    }
    const remainingBps = BigInt(10_000 - maxSlippageBps);
    return (quotedAmountOut * remainingBps) / 10000n;
}
/**
 * Which protocol a pair trades through, from its fee tier.
 *
 * A V3 pool always has a fee tier; a V2 pair never does. This is the only signal
 * the scanner records that distinguishes them, and it is reliable because it
 * comes from the event that created the row — `PoolCreated` carries a fee,
 * `PairCreated` does not.
 *
 * Returns null when it cannot tell, and the caller must treat that as a refusal
 * rather than guessing. Guessing V2 for a V3 pool would build calldata for the
 * wrong router entirely.
 */
export function protocolFromFeeTier(feeTier) {
    if (feeTier === null || feeTier === undefined)
        return 'uniswap_v2';
    if (V3_FEE_TIERS.includes(feeTier))
        return 'uniswap_v3';
    return null;
}
/**
 * Protocols this deployment can actually route a trade through.
 *
 * Not every protocol the scanner recognises is one we can trade. Recognising a
 * pool and being able to swap through it are different capabilities: the first
 * needs a factory event, the second needs a router that implements the
 * protocol's entry point.
 *
 * On Robinhood Chain the only verified router is a Uniswap V2 router — it
 * answers `factory()`, `WETH()` and `getAmountsOut`, and its bytecode contains
 * no `exactInputSingle`, `exactInput` or `WETH9` selector. Every V3 pair sent to
 * it called a function that does not exist and reverted, which surfaced as "the
 * router could not quote this swap" — a message about pool depth, for a problem
 * that had nothing to do with depth.
 *
 * V3 is absent from this list because no V3 router address has been verified
 * against the live chain, not because V3 is unsupported in principle. Adding one
 * means recording the evidence in `docs/CONTRACT_WATCHLIST.md` and adding it
 * here deliberately — CLAUDE.md forbids taking a contract address from a search
 * result, and a wrong router is an address we would send real ETH to.
 */
export const TRADABLE_PROTOCOLS = ['uniswap_v2'];
export function isTradableProtocol(protocol) {
    return protocol !== null && TRADABLE_PROTOCOLS.includes(protocol);
}
//# sourceMappingURL=swap-builder.js.map